// Resources2.js
import { useState } from 'react';
import { BarChart2, DollarSign, Users, Package, ArrowRightCircle, TrendingUp } from 'lucide-react';
import './Resources2.css';
import { useNavigate } from 'react-router-dom';

export default function Resources2() {
  const [hoveredCase, setHoveredCase] = useState(null);
  const navigate = useNavigate();
  const handleMouseEnter = (id) => {
    setHoveredCase(id);
  };

  const handleMouseLeave = () => {
    setHoveredCase(null);
  };
 const handleNavigate = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };
  return (
    <div className="resources2-container">
      {/* Decorative Elements */}
      <div className="decorative-elements">
        <div className="decorative-circle decorative-circle-1"></div>
        <div className="decorative-circle decorative-circle-2"></div>
        <div className="decorative-circle decorative-circle-3"></div>
      </div>

      {/* Hero Section */}
      <header className="resources2-hero-section">
        <div className="r2-hero-content">
          
          <h1 className="r2-main-title">
            How <span className="r2-brand-highlight">ThinkHR</span> Can Be Applied
            <br />
            <span className="r2-subtitle">
              Use Cases for HR Analytics and Predictive Analytics
            </span>
          </h1>
          <p className="r2-intro-text">
            People are every company’s greatest asset—but without data, it’s difficult to make confident, forward-looking workforce decisions. HR analytics provides the insight needed to improve decision accuracy, manage budget constraints, and align talent strategies with business goals. It reveals patterns in hiring, retention, skills gaps, and capacity planning—enabling smarter choices around upskilling, resourcing, and employee development. It also enables smarter workforce planning—whether you're managing headcount, forecasting leave, or evaluating pay structures. As the workplace evolves, HR analytics gives businesses the insight to stay agile, build stronger teams, and stay competitive.
        </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="r2-main-content">
        <div className="r2-content-wrapper">
          <div className="r2-section-intro">
            <div className="r2-section-badge">
              <TrendingUp className="r2-badge-icon" />
              <span className="r2-badge-text">Strategic Insights</span>
            </div>
            
            <p className="r2-emphasis-text">
              Below, we explore practical ways HR analytics can address common challenges and improve decision-making across your business.
            </p>
            <p className="r2-use-case-header"> USE CASES</p>
          </div>

          {/* Use Case Cards */}
          <div className="r2-use-case-grid">
            {/* Use Case 1 */}
            <div 
              className={`r2-use-case-card ${hoveredCase === 1 ? 'hovered' : ''}`}
              onMouseEnter={() => handleMouseEnter(1)}
              onMouseLeave={handleMouseLeave}
            >
              <div className="r2-card-decoration"></div>
              <div className="r2-card-content">
                <div className="r2-case-header">
                  <div className="r2-icon-container">
                    <BarChart2 className="r2-case-icon" />
                  </div>
                  <h2 className="r2-case-title">
                    Managing Workforce Costs
                  </h2>
                </div>
                <p className="r2-case-text">
                  Optimize workforce capacity and control people costs with HR analytics. By bringing together insights on staffing levels, overtime, absenteeism, turnover, and compensation, HR analytics helps you understand whether teams are stretched too thin or operating with excess capacity. It reveals hidden cost drivers and potential risks—like burnout, attrition, or inefficient resource allocation—so you can make informed decisions about hiring, restructuring, or budget planning.
                  <br /><br />
                  <span className="r2-case-highlight">With a clearer picture of your workforce, you can balance performance, well-being, and cost-effectiveness across the organization.</span>
                </p>
              </div>
            </div>

            {/* Use Case 2 */}
            <div 
              className={`r2-use-case-card ${hoveredCase === 2 ? 'hovered' : ''}`}
              onMouseEnter={() => handleMouseEnter(2)}
              onMouseLeave={handleMouseLeave}
            >
              <div className="r2-card-decoration"></div>
              <div className="r2-card-content">
                <div className="r2-case-header">
                  <div className="r2-icon-container">
                    <DollarSign className="r2-case-icon" />
                  </div>
                  <h2 className="r2-case-title">
                    Smarter Talent Strategies From Recruitment to Retention
                  </h2>
                </div>
                <p className="r2-case-text">
                  Strengthen your hiring strategy and boost employee retention with HR analytics. From identifying the right roles and skill levels to benchmarking compensation and benefits, HR analytics helps you stay competitive in attracting top talent. It ensures fair and financially aligned pay structures while highlighting underutilized benefits that can be refined to meet employee needs. On the retention side, analytics surfaces early warning signs of attrition, identifies at-risk employees, and offers data-driven suggestions to improve engagement. 
                  <br /><br />
                  <span className="r2-case-highlight">With a clear view of career progression opportunities and employee sentiment, you can create targeted strategies to keep your best people and reduce turnover.</span>
                </p>
              </div>
            </div>

            {/* Use Case 3 */}
            <div 
              className={`r2-use-case-card ${hoveredCase === 3 ? 'hovered' : ''}`}
              onMouseEnter={() => handleMouseEnter(3)}
              onMouseLeave={handleMouseLeave}
            >
              <div className="r2-card-decoration"></div>
              <div className="r2-card-content">
                <div className="r2-case-header">
                  <div className="r2-icon-container">
                    <Users className="r2-case-icon" />
                  </div>
                  <h2 className="r2-case-title">
                    Build a Future-Ready Workforce
                  </h2>
                </div>
                <p className="r2-case-text">
                  Close talent gaps and build a future-ready workforce through focused upskilling and reskilling. By identifying where skills are lacking across departments and job levels, organisations can align employee development with both current needs and future growth plans. Detailed workforce data helps pinpoint the right individuals based on job role, experience, and performance, making it easier to match people with the right learning opportunities. 
                  <br /><br />
                  <span className="r2-case-highlight">With personalized development plans and clear career paths, employees are more motivated to grow—helping your organization retain valuable talent, strengthen internal capabilities, and reduce dependency on external hiring.</span>
                </p>
              </div>
            </div>

            {/* Use Case 4 */}
            <div 
              className={`r2-use-case-card ${hoveredCase === 4 ? 'hovered' : ''}`}
              onMouseEnter={() => handleMouseEnter(4)}
              onMouseLeave={handleMouseLeave}
            >
              <div className="r2-card-decoration"></div>
              <div className="r2-card-content">
                <div className="r2-case-header">
                  <div className="r2-icon-container">
                    <Package className="r2-case-icon" />
                  </div>
                  <h2 className="r2-case-title">
                    Strategic Capacity Planning
                  </h2>
                </div>
                <p className="r2-case-text">
                  Use payroll data to drive smarter capacity planning. Payroll insights go beyond compensation—they reveal patterns in turnover, hiring needs, and workforce shifts. By analysing this data, HR teams can forecast staffing requirements, adjust schedules, and respond proactively to changing workforce demands. This leads to more informed decisions around hiring, employee performance, and resource planning. 
                  <br /><br />
                  <span className="r2-case-highlight">Ultimately, leveraging payroll trends enables better workforce management, helping improve operations while enhancing the overall employee experience.</span>
                </p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="r2-cta-section">
            <div className="r2-cta-content">
              <h3 className="r2-cta-title">
                Use data to make smarter workforce decisions that drive success. As your business evolves, HR analytics ensures your workforce is ready for the future.
              </h3>
              <p className="r2-cta-text">
                Let’s get started and contact us to see how we can help. 
              </p>
              <button className="r2-cta-button"onClick={() => handleNavigate('/contactus')}>
                <span>Contact Us  </span>
                {/*<ArrowRightCircle className="r2-button-icon" />*/}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}